import type React from 'react';
import type { VideoMetadata } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { clipboardImageFile } from './tableImport';
import { externalVideoUrl } from './externalVideoUrl';
import { isVideoFile } from './useCardVideoUpload';
import type { useLibraryVideoDrop } from './useLibraryVideoDrop';

interface UseAddSlideCardInteractionsArgs {
  adding: boolean;
  onFile: (file: File) => void;
  onVideoUrl?: (url: string) => void;
  onVideoFile?: (file: File, metadata: VideoMetadata) => void;
  videoDrop: ReturnType<typeof useLibraryVideoDrop>;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Drag/drop, paste, keyboard-activation, and file-input handlers for `AddSlideCard` — bundled
 * together since they all route the same incoming file/URL/video to the same few callbacks.
 */
export function useAddSlideCardInteractions({
  adding,
  onFile,
  onVideoUrl,
  onVideoFile,
  videoDrop,
  inputRef,
}: UseAddSlideCardInteractionsArgs) {
  const open = () => inputRef.current?.click();

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  };

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    videoDrop.onDragOver(event);
  };

  const onDrop = (event: React.DragEvent, onStashVideo: (file: File) => void) => {
    event.preventDefault();
    if (videoDrop.handleDrop(event) || adding) return;
    const file = event.dataTransfer.files[0];
    if (file && isVideoFile(file) && onVideoFile) onStashVideo(file);
    else if (file && file.type.startsWith('image/')) onFile(file);
  };

  const onPaste = (event: React.ClipboardEvent) => {
    const file = clipboardImageFile(event.clipboardData);
    if (file) {
      event.preventDefault();
      onFile(file);
      return;
    }
    const videoUrl = onVideoUrl && externalVideoUrl(event.clipboardData?.getData('text/plain'));
    if (!videoUrl) return;
    event.preventDefault();
    onVideoUrl?.(videoUrl);
  };

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onStashVideo: (file: File) => void,
  ) => {
    const file = event.target.files?.[0];
    if (file && isVideoFile(file) && onVideoFile) onStashVideo(file);
    else if (file) onFile(file);
    event.target.value = '';
  };

  return { open, onKeyDown, onDragOver, onDrop, onPaste, onInputChange };
}
